/**
 * Check-In Data Models
 *
 * TypeScript interfaces for CheckIn entity and related models.
 * Provides type-safe data structures for check-in creation, status management, and state transitions.
 *
 * @see WI-020 - Check-In Data Models
 * @see data-schemas.md Section: CheckIn
 */

import { Timestamp } from './user.models';

/**
 * Check-In Status (Phase 2)
 * Represents the current state of a check-in in the lifecycle.
 */
export type CheckInStatus =
  | 'scheduled'   // Created, waiting for trigger time
  | 'active'      // Trigger time reached, waiting for user check-in
  | 'resolved'    // User checked in successfully
  | 'cancelled';  // User cancelled before trigger time

/**
 * Extended Check-In Status (Phase 3+)
 * Additional statuses for nudges, escalation, and snoozing.
 * Not used in Phase 2, but defined for future compatibility.
 */
export type ExtendedCheckInStatus = CheckInStatus
  | 'nudge'       // User nudged, still waiting
  | 'escalating'  // Grace window passed, notifying contacts
  | 'completed'   // Contacts notified or acknowledged
  | 'snoozed';    // User snoozed check-in

/**
 * Status History Entry
 * Tracks state transitions for audit trail.
 */
export interface StatusHistoryEntry {
  status: ExtendedCheckInStatus;
  timestamp: Timestamp;
  reason?: string;  // Optional reason for status change (e.g., "User cancelled", "Timeout")
}

/**
 * CheckIn
 * Main check-in document stored at /users/{userId}/checkIns/{checkInId} in Firestore.
 */
export interface CheckIn {
  id: string;                    // UUID
  userId: string;                // Foreign key to User
  type: 'time';                  // Phase 2: 'time' only (Phase 3+: 'geofence')

  // Trigger configuration
  triggerTime: Timestamp;        // UTC when check-in becomes active
  graceWindow: number;           // Minutes after trigger before escalation (default: 30)
  nudgeDelay: number;            // Minutes after trigger for first nudge (default: 15)

  // State machine (Phase 3+: uses ExtendedCheckInStatus for snooze support)
  status: ExtendedCheckInStatus;
  statusHistory: StatusHistoryEntry[];

  // Escalation plan (Phase 4)
  contactIds: string[];          // Array of Contact IDs (empty in Phase 2)

  // User context
  note?: string;                 // User-provided context (e.g., "Driving to Portland")

  // Activity tracking
  lastUserActionAt?: Timestamp;  // Last time user interacted with check-in

  // Timestamps
  createdAt: Timestamp;
  resolvedAt?: Timestamp;
  completedAt?: Timestamp;

  // Idempotency
  dedupeKey: string;             // For preventing duplicate check-ins
}

/**
 * Create CheckIn Request
 * Payload for creating a new check-in (client to service).
 */
export interface CreateCheckInRequest {
  triggerTime: Date;            // Local time, converted to UTC by service
  note?: string;                // Optional user-provided context
  graceWindow?: number;         // Optional, defaults to user setting (30 min)
  nudgeDelay?: number;          // Optional, defaults to user setting (15 min)
  contactIds?: string[];        // Optional, empty in Phase 2
}

/**
 * Update CheckIn Request
 * Payload for updating an existing check-in.
 */
export interface UpdateCheckInRequest {
  status?: ExtendedCheckInStatus;  // Update status (subject to state machine validation)
  note?: string;                   // Update note
  lastUserActionAt?: Date;         // Update last user action time
}

/**
 * CheckIn Filters
 * Query parameters for filtering check-ins.
 */
export interface CheckInFilters {
  status?: ExtendedCheckInStatus | ExtendedCheckInStatus[];  // Filter by single status or array
  startDate?: Date;             // Filter by trigger time >= startDate
  endDate?: Date;               // Filter by trigger time <= endDate
  limit?: number;               // Limit number of results
}

// ==================== HELPER FUNCTIONS ====================

/**
 * Check if check-in is currently active.
 * Active statuses include 'active', 'nudge', 'escalating'.
 *
 * @param checkIn - CheckIn entity
 * @returns true if check-in is in an active state
 */
export function isCheckInActive(checkIn: CheckIn): boolean {
  return ['active', 'nudge', 'escalating'].includes(checkIn.status);
}

/**
 * Check if check-in is resolved/completed.
 * Resolved statuses include 'resolved', 'completed', 'cancelled'.
 *
 * @param checkIn - CheckIn entity
 * @returns true if check-in is in a terminal state
 */
export function isCheckInResolved(checkIn: CheckIn): boolean {
  return ['resolved', 'completed', 'cancelled'].includes(checkIn.status);
}

/**
 * Get CSS variable name for check-in status color.
 * Returns reference to design token from theme/variables.scss.
 *
 * IMPORTANT: Do NOT return hardcoded hex values. Return CSS variable names.
 * Components must use these via `var(--color-status-active)` in templates.
 *
 * @param status - ExtendedCheckInStatus
 * @returns CSS variable name (e.g., '--color-status-active')
 *
 * @see ionic-styling-architecture.md Section 2.2 (Domain-Specific Semantic Colors)
 */
export function getStatusColor(status: ExtendedCheckInStatus): string {
  const colorMap: Record<ExtendedCheckInStatus, string> = {
    scheduled: '--color-status-scheduled',   // #94A3B8 (Medium Gray)
    active: '--color-status-active',         // #14B8A6 (Soft Teal)
    resolved: '--color-status-resolved',     // #10B981 (Jade)
    cancelled: '--color-status-cancelled',   // #64748B (Slate Gray)
    nudge: '--color-status-nudge',           // #F59E0B (Amber)
    escalating: '--color-status-escalating', // #F59E0B (Amber)
    completed: '--color-status-completed',   // #10B981 (Jade)
    snoozed: '--color-status-snoozed',       // #94A3B8 (Medium Gray)
  };
  return colorMap[status] || '--ion-color-medium';
}

/**
 * Get human-readable label for check-in status.
 * Used for UI display (e.g., status badges, list items).
 *
 * @param status - ExtendedCheckInStatus
 * @returns Display label (e.g., "Checked in")
 */
export function getStatusLabel(status: ExtendedCheckInStatus): string {
  const labelMap: Record<ExtendedCheckInStatus, string> = {
    scheduled: 'Scheduled',
    active: 'Active',
    resolved: 'Checked in',
    cancelled: 'Cancelled',
    nudge: 'Nudge sent',
    escalating: 'Escalating',
    completed: 'Completed',
    snoozed: 'Snoozed',
  };
  return labelMap[status] || status;
}

/**
 * Format Firestore Timestamp to locale string.
 * Converts Timestamp to JavaScript Date and formats using browser locale.
 *
 * @param timestamp - Firestore Timestamp
 * @returns Formatted date/time string (e.g., "10/5/2025, 6:00:00 PM")
 */
export function formatCheckInTime(timestamp: Timestamp): string {
  const date = new Date(timestamp.seconds * 1000);
  return date.toLocaleString();
}

/**
 * Generate dedupe key for check-in idempotency.
 * Prevents duplicate check-ins for same user/time.
 *
 * Format: "{userId}_{ISO8601_timestamp}"
 * Example: "abc123_2025-10-05T18:00:00.000Z"
 *
 * @param userId - User ID
 * @param triggerTime - Check-in trigger time
 * @returns Dedupe key string
 */
export function generateDedupeKey(userId: string, triggerTime: Date): string {
  const timeStr = triggerTime.toISOString();
  return `${userId}_${timeStr}`;
}
